import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Star } from 'lucide-react';
import { getAllReviews } from '../../../services/reviewService';
import './HomeSections.css';

const COUNT = 6;

const initialsOf = name =>
  (name || '?')
    .split(' ')
    .map(part => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const Stars = ({ rating }) => (
  <span className="hs-stars" role="img" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(n => (
      <Star
        key={n}
        aria-hidden="true"
        className={n <= Math.round(rating) ? 'is-on' : undefined}
      />
    ))}
  </span>
);

const HomeReviews = ({ products = [] }) => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getAllReviews()
      .then(response => {
        if (!cancelled && response?.success && Array.isArray(response.data)) {
          setReviews(response.data);
        }
      })
      .catch(() => {
        // The section stays hidden when reviews can't be loaded.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const productNames = useMemo(
    () => new Map(products.map(p => [p.id, p.name])),
    [products]
  );

  const rated = reviews.filter(r => Number.isFinite(Number(r.rating)));
  const average = rated.length
    ? rated.reduce((sum, r) => sum + Number(r.rating), 0) / rated.length
    : 0;

  const shown = useMemo(
    () =>
      [...reviews]
        .filter(r => r.comment)
        .sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        )
        .slice(0, COUNT),
    [reviews]
  );

  if (!shown.length) return null;

  return (
    <section className="hs" aria-labelledby="hs-reviews-title">
      <div className="hs__inner">
        <div className="hs__head">
          <div>
            <h2 id="hs-reviews-title" className="hs__title">
              What buyers say
            </h2>
            {rated.length > 0 && (
              <p className="hs__sub hs-rating">
                <Stars rating={average} />
                {average.toFixed(1)} average from {rated.length}{' '}
                {rated.length === 1 ? 'review' : 'reviews'}
              </p>
            )}
          </div>
          <Link className="hs__link" to="/reviews">
            Read all reviews
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>

        <ul className="hs-grid">
          {shown.map(review => {
            const project = productNames.get(review.productId);
            return (
              <li key={review.id}>
                <figure className="hs-review">
                  <Stars rating={Number(review.rating) || 0} />
                  <blockquote className="hs-review__quote">
                    {review.comment}
                  </blockquote>
                  <figcaption className="hs-review__foot">
                    <span className="hs-review__avatar" aria-hidden="true">
                      {initialsOf(review.userName)}
                    </span>
                    <span className="hs-review__who">
                      <span className="hs-review__name">
                        {review.userName || 'Buyer'}
                      </span>
                      <span className="hs-review__detail">
                        {[project, formatDate(review.createdAt)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    {review.isVerifiedPurchase && (
                      <span className="hs-review__verified">
                        <BadgeCheck aria-hidden="true" />
                        Verified
                      </span>
                    )}
                  </figcaption>
                </figure>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default HomeReviews;
